function debounce(func, delay) {
	let timeoutId;
	return function (...args) {
		clearTimeout(timeoutId);
		timeoutId = setTimeout(() => {
			func.apply(this, args);
		}, delay);
	};
}

const initDocumentationSearch = () => {
	const searchInputs = document.querySelectorAll('.site-search-common-input');
	if (!searchInputs.length) return;

	const searchForms = document.querySelectorAll('form.documentation-search');
	searchForms.forEach((form) => {
		form.addEventListener('submit', (event) => {
			event.preventDefault();
		});
	});

	let searchRequestId = 0;

	const setSearchLoading = (isLoading) => {
		document.querySelectorAll('.documentation-search-wrapper').forEach((wrapper) => {
			wrapper.classList.toggle('is-loading', isLoading);
		});

		document.querySelectorAll('form.documentation-search').forEach((form) => {
			form.classList.toggle('is-loading', isLoading);
			form.setAttribute('aria-busy', isLoading ? 'true' : 'false');
		});

		if (!isLoading) {
			return;
		}

		const searchBoxes = document.querySelectorAll('.documentation-search-results');
		searchBoxes.forEach((searchBox) => {
			searchBox.style.display = 'block';
			searchBox.innerHTML = `
				<span class="site-docs-search-loading" role="status" aria-live="polite">
					<span class="site-docs-search-spinner" aria-hidden="true"></span>
					<span class="site-docs-search-loading-text">Searching…</span>
				</span>
			`;
		});
	};

	const betterSearch = debounce(async (e) => {
		const search = e.target.value.trim();
		const requestId = ++searchRequestId;

		if (!search) {
			setSearchLoading(false);
			searchSuggestions(search, []);
			return;
		}

		if (!window.docsAjax || !window.docsAjax.ajax_url) {
			setSearchLoading(false);
			return;
		}

		setSearchLoading(true);

		try {
			const formData = new FormData();
			formData.append('search', search);
			formData.append('action', 'search_docs');
			formData.append('nonce', window.docsAjax.nonce);

			const response = await fetch(window.docsAjax.ajax_url, {
				method: 'POST',
				body: formData,
				credentials: 'same-origin',
			});

			const json = await response.json();
			if (requestId !== searchRequestId) {
				return;
			}

			const searchData = json && json.success && Array.isArray(json.data) ? json.data : [];
			setSearchLoading(false);
			searchSuggestions(search, searchData);
		} catch (error) {
			if (requestId !== searchRequestId) {
				return;
			}

			setSearchLoading(false);
			searchSuggestions(search, []);
		}
	}, 500);

	searchInputs.forEach((input) => {
		input.addEventListener('input', betterSearch);
		input.addEventListener('keyup', betterSearch);
	});
};

const searchSuggestions = (search, searchData) => {
	const searchBoxes = document.querySelectorAll('.documentation-search-results');
	if (!searchBoxes.length) return;

	const results = Array.isArray(searchData) ? searchData : [];
	let html = '';

	if (search && results.length) {
		results.forEach((item) => {
			const { title, link } = item;
			const escapedSearch = search.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
			const regex = new RegExp(escapedSearch, 'gi');
			const highlightedTitle = title.replace(
				regex,
				`<span class="site-searched-text">${search}</span>`,
			);

			html += `
				<a href="${link}" class="site-search-suggestions-item">
					${highlightedTitle}
				</a>
			`;
		});
	}

	searchBoxes.forEach((searchBox) => {
		if (!search) {
			searchBox.style.display = 'none';
			searchBox.innerHTML = '';
			return;
		}

		searchBox.style.display = 'block';
		searchBox.innerHTML =
			results.length === 0
				? `<span class="site-docs-search-no-results">No results found</span>`
				: html;
	});
};

const initCodeHighlighting = () => {
	const content = document.querySelector('.documentation-content');
	if (!content || typeof window.Prism === 'undefined') {
		return;
	}

	const detectLanguage = (code) => {
		const trimmed = code.trim();

		if (trimmed.includes('<?php') || trimmed.includes('<?=') || /\becho\s*\(/.test(trimmed)) {
			return 'php';
		}

		if (trimmed.startsWith('<!DOCTYPE') || /^<\/?[a-z]/i.test(trimmed)) {
			return 'markup';
		}

		if (/^(npm|yarn|pnpm|composer|wp)\s/.test(trimmed) || trimmed.startsWith('#!/bin/bash')) {
			return 'bash';
		}

		if (
			(trimmed.startsWith('{') && trimmed.endsWith('}')) ||
			(trimmed.startsWith('[') && trimmed.endsWith(']'))
		) {
			try {
				JSON.parse(trimmed);
				return 'json';
			} catch (error) {
				// Fall through to default language.
			}
		}

		if (/\b(SELECT|INSERT|UPDATE|DELETE|FROM|WHERE)\b/i.test(trimmed)) {
			return 'sql';
		}

		if (/\b(import|export|const|let|var|function)\b/.test(trimmed)) {
			return 'javascript';
		}

		return 'markup';
	};

	const blocks = content.querySelectorAll('pre code, .wp-block-code code, .wp-block-preformatted code');

	blocks.forEach((block) => {
		const pre = block.closest('pre');
		if (pre) {
			pre.classList.add('documentation-code-block');
		}

		let languageClass = [...block.classList].find((className) => className.startsWith('language-'));
		if (!languageClass && pre) {
			languageClass = [...pre.classList].find((className) => className.startsWith('language-'));
		}

		if (!languageClass) {
			languageClass = `language-${detectLanguage(block.textContent || '')}`;
			block.classList.add(languageClass);
		}

		if (pre && !pre.classList.contains(languageClass)) {
			pre.classList.add(languageClass);
		}

		try {
			window.Prism.highlightElement(block);
		} catch (error) {
			console.error('Prism highlight failed', error);
		}
	});

	content.querySelectorAll('code:not(pre code)').forEach((inlineCode) => {
		inlineCode.classList.add('documentation-inline-code');
	});
};

const initMobileMenu = () => {
	const menuSearchIcon = document.querySelector('.mobile-menu-search');
	const menuSearchForm = document.querySelector('#documentation-search-mobile');
	const menuSearchCloseIcon = document.querySelector('.search-close-icon');
	const mobileMenuHeader = document.querySelector('.documentation-mobile-inner-menu-header');
	const menuPageName = document.querySelector('.menu-page-name');
	const menuWrapper = document.querySelector('.documentation-mobile-menu');
	const menuCloseIcon = document.querySelector('.documentation-mobile-menu-close-icon');
	const menuOverlay = document.querySelector('.documentation-mobile-menu-overlay');

	const closeMobileMenu = () => {
		if (menuWrapper) {
			menuWrapper.classList.remove('active');
		}
		document.body.classList.remove('documentation-mobile-menu-open');
	};

	const openMobileMenu = () => {
		if (menuWrapper) {
			menuWrapper.classList.add('active');
		}
		document.body.classList.add('documentation-mobile-menu-open');
	};

	if (menuSearchIcon && menuSearchForm && mobileMenuHeader) {
		menuSearchIcon.addEventListener('click', function () {
			mobileMenuHeader.classList.remove('active');
			menuSearchForm.classList.add('active');
			const input = menuSearchForm.querySelector('.site-search-common-input');
			if (input) {
				input.focus();
			}
		});
	}

	if (menuSearchCloseIcon && menuSearchForm && mobileMenuHeader) {
		menuSearchCloseIcon.addEventListener('click', function () {
			mobileMenuHeader.classList.add('active');
			menuSearchForm.classList.remove('active');
		});
	}

	if (menuPageName && menuWrapper) {
		menuPageName.addEventListener('click', openMobileMenu);
	}

	if (menuCloseIcon) {
		menuCloseIcon.addEventListener('click', closeMobileMenu);
	}

	if (menuOverlay) {
		menuOverlay.addEventListener('click', closeMobileMenu);
	}
};

const initAccordions = () => {
	const accordions = document.querySelectorAll('.documentation-accordion');
	const getAccordionContent = (accordion) =>
		accordion.querySelector(':scope > .documentation-accordion-content-wrapper');

	const collapseAccordion = (accordion) => {
		const content = getAccordionContent(accordion);
		if (!content) {
			return Promise.resolve();
		}

		return new Promise((resolve) => {
			let done = false;
			const finish = () => {
				if (done) return;
				done = true;
				accordion.classList.remove('active');
				content.style.height = '0';
				content.style.display = '';
				resolve();
			};

			content.style.display = 'block';
			content.style.height = `${content.scrollHeight}px`;
			void content.offsetHeight;
			content.style.height = '0';

			const onEnd = (event) => {
				if (event.propertyName !== 'height') return;
				content.removeEventListener('transitionend', onEnd);
				finish();
			};

			content.addEventListener('transitionend', onEnd);
			setTimeout(finish, 350);
		});
	};

	const expandAccordion = (accordion) => {
		const content = getAccordionContent(accordion);
		if (!content) return;

		accordion.classList.add('active');
		content.style.display = 'block';
		content.style.height = '0';
		void content.offsetHeight;

		const height = content.scrollHeight;
		content.style.height = `${height}px`;

		const onEnd = (event) => {
			if (event.propertyName !== 'height') return;
			content.removeEventListener('transitionend', onEnd);
			content.style.height = 'auto';

			// Recalculate ancestor accordion heights after nested expand.
			let parent = accordion.parentElement;
			while (parent) {
				const parentAccordion = parent.closest('.documentation-accordion');
				if (!parentAccordion) break;
				const parentContent = getAccordionContent(parentAccordion);
				if (
					parentContent &&
					parentAccordion.classList.contains('active') &&
					parentContent.parentElement === parentAccordion
				) {
					parentContent.style.height = 'auto';
				}
				parent = parentAccordion.parentElement;
			}
		};

		content.addEventListener('transitionend', onEnd);
		setTimeout(() => {
			if (accordion.classList.contains('active')) {
				content.style.height = 'auto';
			}
		}, 350);
	};

	const getSiblingAccordions = (accordion) => {
		const parent = accordion.parentElement;
		if (!parent) return [];
		return [...parent.children].filter((el) => el.classList.contains('documentation-accordion'));
	};

	accordions.forEach((accordion) => {
		const header = accordion.querySelector(':scope > .documentation-accordion-header');
		if (!header) return;

		// Ensure initially active accordions measure correctly after nested content loads.
		if (accordion.classList.contains('active')) {
			const content = getAccordionContent(accordion);
			if (content) {
				content.style.height = 'auto';
			}
		}

		header.addEventListener('click', async function () {
			const isActive = accordion.classList.contains('active');
			const isTopLevel = !!accordion.closest('.documentation-accordion-container') &&
				accordion.parentElement?.classList.contains('documentation-accordion-container');

			// Nested categories still close siblings; parent categories stay independently open.
			if (!isTopLevel) {
				const siblingAccordions = getSiblingAccordions(accordion);
				for (const otherAccordion of siblingAccordions) {
					if (otherAccordion !== accordion && otherAccordion.classList.contains('active')) {
						await collapseAccordion(otherAccordion);
					}
				}
			}

			if (!isActive) {
				expandAccordion(accordion);
			} else {
				await collapseAccordion(accordion);
			}
		});
	});

	window.addEventListener('resize', () => {
		accordions.forEach((accordion) => {
			if (!accordion.classList.contains('active')) return;
			const content = getAccordionContent(accordion);
			if (content) {
				content.style.height = 'auto';
			}
		});
	});
};

const initDynamicStickyMenu = () => {
	const stickyMenuWrapper = document.querySelector('.site-table-of-contents-items');
	if (!stickyMenuWrapper) return;

	const content = document.querySelector('.documentation-content');
	if (!content) return;

	const uniqueHeaders = [...content.querySelectorAll('h2, h3')].filter(
		(heading) => !heading.classList.contains('documentation-page-title'),
	);

	if (!uniqueHeaders.length) {
		stickyMenuWrapper.innerHTML = '';
		return;
	}

	const handleActiveClass = (id) => {
		const items = stickyMenuWrapper.querySelectorAll('.site-table-of-contents-item');
		items.forEach((item) => {
			item.classList.remove('site-active');
			if (item.getAttribute('data-href') === `#${id}`) {
				item.classList.add('site-active');
			}
		});
	};

	const getHeadingText = (header) => {
		const clone = header.cloneNode(true);
		clone.querySelectorAll('span').forEach((span) => {
			if (span.querySelector('svg')) {
				span.remove();
			}
		});
		return clone.textContent.trim();
	};

	let html = '';
	uniqueHeaders.forEach((header, index) => {
		let id = header.getAttribute('id');

		if (!id) {
			id = `wp-block-heading-${index}`;
			header.setAttribute('id', id);
		}

		const level = header.tagName.toLowerCase();
		const isH3 = level === 'h3';
		const title = getHeadingText(header);

		const svgIcon = document.createElement('span');
		const copyIcon = `
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" style="width: 20px;position: relative;"><path d="M 21.75 4 C 20.078125 4 18.492188 4.660156 17.3125 5.84375 L 15.84375 7.3125 C 14.660156 8.496094 14 10.078125 14 11.75 C 14 12.542969 14.152344 13.316406 14.4375 14.03125 L 16.0625 12.40625 C 15.859375 11.109375 16.253906 9.714844 17.25 8.71875 L 18.71875 7.25 C 19.523438 6.445313 20.613281 6 21.75 6 C 22.886719 6 23.945313 6.445313 24.75 7.25 C 26.410156 8.910156 26.410156 11.621094 24.75 13.28125 L 23.28125 14.75 C 22.476563 15.554688 21.386719 16 20.25 16 C 20.027344 16 19.808594 15.976563 19.59375 15.9375 L 17.96875 17.5625 C 18.683594 17.847656 19.457031 18 20.25 18 C 21.921875 18 23.507813 17.339844 24.6875 16.15625 L 26.15625 14.6875 C 27.339844 13.503906 28 11.921875 28 10.25 C 28 8.578125 27.339844 7.027344 26.15625 5.84375 C 24.976563 4.660156 23.421875 4 21.75 4 Z M 19.28125 11.28125 L 11.28125 19.28125 L 12.71875 20.71875 L 20.71875 12.71875 Z M 11.75 14 C 10.078125 14 8.492188 14.660156 7.3125 15.84375 L 5.84375 17.3125 C 4.660156 18.496094 4 20.078125 4 21.75 C 4 23.421875 4.660156 24.972656 5.84375 26.15625 C 7.023438 27.339844 8.578125 28 10.25 28 C 11.921875 28 13.507813 27.339844 14.6875 26.15625 L 16.15625 24.6875 C 17.339844 23.503906 18 21.921875 18 20.25 C 18 19.457031 17.847656 18.683594 17.5625 17.96875 L 15.9375 19.59375 C 16.140625 20.890625 15.746094 22.285156 14.75 23.28125 L 13.28125 24.75 C 12.476563 25.554688 11.386719 26 10.25 26 C 9.113281 26 8.054688 25.554688 7.25 24.75 C 5.589844 23.089844 5.589844 20.378906 7.25 18.71875 L 8.71875 17.25 C 9.523438 16.445313 10.613281 16 11.75 16 C 11.972656 16 12.191406 16.023438 12.40625 16.0625 L 14.03125 14.4375 C 13.316406 14.152344 12.542969 14 11.75 14 Z"></path></svg>`;
		const copiedIcon = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" style="width: 20px;position: relative;"><path d="M 28.28125 6.28125 L 11 23.5625 L 3.71875 16.28125 L 2.28125 17.71875 L 10.28125 25.71875 L 11 26.40625 L 11.71875 25.71875 L 29.71875 7.71875 Z"></path></svg>`;

		svgIcon.className = 'documentation-heading-copy';
		svgIcon.innerHTML = copyIcon;
		svgIcon.style =
			'display: inline-flex ; justify-content: center; align-items: center; margin-left: 8px;cursor:pointer;';
		header.append(svgIcon);

		svgIcon.addEventListener('click', function (event) {
			event.stopPropagation();
			const url = `${window.location.origin}${window.location.pathname}#${id}`;
			navigator.clipboard
				.writeText(url)
				.then(() => {
					svgIcon.innerHTML = copiedIcon;
					setTimeout(() => {
						svgIcon.innerHTML = copyIcon;
					}, 2000);
				})
				.catch((err) => console.error('Copy failed', err));
		});

		html += `<a class="site-table-of-contents-item ${isH3 ? 'is-h3' : ''} ${
			index === 0 ? 'site-active' : ''
		}" href="javascript:void(0);" data-href="#${id}">${title}</a>`;
	});

	stickyMenuWrapper.innerHTML = html;

	const menuList = stickyMenuWrapper.querySelectorAll('.site-table-of-contents-item');
	menuList.forEach((menu, index) => {
		menu.addEventListener('click', () => {
			window.scrollTo({
				top: uniqueHeaders[index].getBoundingClientRect().top + window.pageYOffset - 77,
				behavior: 'smooth',
			});
			setTimeout(() => {
				handleActiveClass(uniqueHeaders[index].getAttribute('id'));
			}, 100);
		});
	});

	window.addEventListener('scroll', () => {
		let current = '';
		const scrollPos = window.pageYOffset;
		uniqueHeaders.forEach((header, index, headers) => {
			const top = header.getBoundingClientRect().top + window.pageYOffset - 77;
			const nextHeader = headers.length > index + 1 ? headers[index + 1] : null;
			const nextTop = nextHeader
				? nextHeader.getBoundingClientRect().top + window.pageYOffset
				: null;

			if (scrollPos >= top && (nextTop === null || scrollPos < nextTop)) {
				current = header.getAttribute('id');
			}
		});

		handleActiveClass(current);
	});
};

jQuery(document).ready(function () {
	initDocumentationSearch();
});

document.addEventListener('DOMContentLoaded', function () {
	initMobileMenu();
	initAccordions();
	initDynamicStickyMenu();
	initCodeHighlighting();
});
